// services/appointmentService.js
import axios from 'axios';
import moment from 'moment';
import toast from 'react-hot-toast';
import { supabase } from '../data/supabase';
import {getInitials} from '../utils/nameInitial';

export const appointmentService = {
  /**
   * Obtém o saldo atual do paciente para um tipo específico de sessão
   * @param {string} patientId - ID do paciente
   * @param {string} sessionType - Tipo da sessão (individual, casal, familia)
   * @returns {Promise<Object>} - Dados do saldo
   */
  async getPatientCreditBalance(patientId, sessionType) {
    try {
      // Buscar carteiras do paciente
      const { data: wallets, error: walletsError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId);
      
      if (walletsError) throw walletsError;
      
      if (!wallets || wallets.length === 0) {
        return { available: 0, total: 0, used: 0 };
      }
      
      // Buscar transações para calcular saldo
      const { data: transactions, error: transactionsError } = await supabase
        .from('credit_transactions')
        .select(`
          *,
          wallet:wallet_id(*)
        `)
        .in('wallet_id', wallets.map(w => w.id));
      
      if (transactionsError) throw transactionsError;
      
      // Filtrar transações pelo tipo de sessão
      const filteredTransactions = (transactions || []).filter(t => 
        t.wallet?.type === sessionType || 
        (t.notes && t.notes.toLowerCase().includes(sessionType.toLowerCase()))
      );
      
      // Calcular saldo
      const totalCredits = filteredTransactions.reduce((sum, t) => 
        sum + (t.amount > 0 ? t.amount : 0), 0);
        
      const usedCredits = filteredTransactions.reduce((sum, t) => 
        sum + (t.amount < 0 ? -t.amount : 0), 0);
        
      const availableCredits = totalCredits - usedCredits;
      
      return {
        available: availableCredits,
        total: totalCredits,
        used: usedCredits,
        wallets: wallets,
        transactions: filteredTransactions
      };
    } catch (error) {
      console.error('Erro ao buscar saldo de créditos:', error);
      throw error;
    }
  },

  /**
   * Debita um crédito da carteira do paciente
   * @param {string} patientId - ID do paciente
   * @param {string} sessionType - Tipo da sessão
   * @param {string} appointmentId - ID do agendamento
   * @returns {Promise<boolean>} - Sucesso da operação
   */
  async debitCredit(patientId, sessionType, appointmentId) {
    try {
      // Buscar carteira apropriada
      const { data: wallets } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId)
        .eq('type', sessionType)
        .limit(1);
      
      let walletId = wallets?.[0]?.id;
      
      // Se não encontrar carteira específica, buscar carteira genérica
      if (!walletId) {
        const { data: genericWallets } = await supabase
          .from('wallet')
          .select('*')
          .eq('patient_id', patientId)
          .limit(1);
        
        walletId = genericWallets?.[0]?.id;
      }
      
      if (!walletId) {
        throw new Error('Carteira não encontrada');
      }
      
      // Registrar transação de débito
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletId,
          amount: -1, // Débito de 1 crédito
          transaction_type: 'appointment',
          source_type: 'individual_credit', // Assumindo crédito individual por padrão
          appointment_id: appointmentId,
          notes: `Agendamento de sessão ${sessionType}`,
          created_at: new Date().toISOString()
        });
      
      if (transactionError) throw transactionError;
      
      return true;
    } catch (error) {
      console.error('Erro ao debitar crédito:', error);
      throw error;
    }
  },

  /**
   * Registra um agendamento no banco de dados
   */
  async registerAppointment(message, patientId, startTime, endTime, category, meetLink) {
    try {
      const { data, error } = await supabase
        .from('appointment')
        .upsert({
          specialist_id: message.idSpec,
          patient_id: patientId,
          status: 'confirmed',
          start: startTime.toISOString(),
          end: endTime.toISOString(),
          start_history: startTime.toISOString(),
          end_history: endTime.toISOString(),
          category: category,
          meetLink: meetLink,
          is_package: false
        })
        .select();
      
      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Erro ao registrar agendamento:', error);
      throw error;
    }
  },
  
  /**
   * Cria um agendamento usando créditos individuais
   */
  async createAppointment(message, closeModal, user, balance, typeVoucher) {
    try {
      // Obter dados do paciente
      const { data: { user: userPatient } } = await supabase.auth.getUser();
      const { data: patient } = await supabase
        .from('patient')
        .select()
        .eq('id_user', userPatient.id);
      
      if (!patient || patient.length === 0) {
        return toast.error('Dados do paciente não encontrados');
      }
      
      // Verificar saldo usando o novo sistema
      const creditBalance = await this.getPatientCreditBalance(patient[0].id, typeVoucher);
      
      if (creditBalance.available <= 0) {
        return toast.error('Saldo insuficiente para este tipo de sessão');
      }
      
      // Calcular horários
      const endTime = moment(message.time, 'HH:mm').add(50, 'minutes').format('HH:mm');
      const startTime = moment(message.time, 'HH:mm').format('HH:mm');
      const dateDome = moment(message.selectedData).format('YYYY-MM-DD');
      
      const momentObjStart = moment(`${dateDome} ${startTime}`, 'YYYY-MM-DD HH:mm');
      const momentObjEnd = moment(`${dateDome} ${endTime}`, 'YYYY-MM-DD HH:mm');
      
      // Criar evento no Google Calendar
      const link = await this.createGoogleCalendarEvent(
        momentObjStart,
        momentObjEnd,
        message,
        user,
        patient[0],
        typeVoucher
      );
      
      // Registrar agendamento
      const appointmentData = await this.registerAppointment(
        message,
        patient[0].id,
        momentObjStart,
        momentObjEnd,
        typeVoucher,
        link
      );
      
      if (!appointmentData || !appointmentData.data) {
        return toast.error('Erro ao registrar agendamento');
      }
      
      // Debitar crédito
      const debitSuccess = await this.debitCredit(
        patient[0].id,
        typeVoucher,
        appointmentData.data[0].id
      );
      
      if (debitSuccess) {
        message.setTimeMeeting && message.setTimeMeeting(message.time);
        toast.success("Agendamento Concluído!");
        closeModal();
        window.location.href = "/consultas";
      } else {
        // Reverter agendamento se não conseguir debitar
        await supabase
          .from('appointment')
          .delete()
          .eq('id', appointmentData.data[0].id);
        toast.error('Erro ao processar pagamento do crédito');
      }
      
    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
      toast.error('Algo deu errado, tente novamente!');
    }
  },

 async createGoogleCalendarEvent(start, end, message, user, patient, typeVoucher) {
    const event = {
      start: { dateTime: start.toISOString() },
      end: { dateTime: end.toISOString() },
      summary: `Consulta com ${getInitials(patient.name)}`,
      description: `Terapia ${typeVoucher}`,
      // Solicita criação automática do Meet
      conferenceData: {
        createRequest: {
          requestId: `meet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          conferenceSolutionKey: {
            type: 'hangoutsMeet'
          }
        }
      },
      // Configurações adicionais
      attendees: [
        { email: patient.email },
        { email: user.email }
      ],
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'email', minutes: 24 * 60 }, // 1 dia antes
          { method: 'popup', minutes: 30 }       // 30 min antes
        ]
      }
    };
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL_BACKEND}/api/insert-event`,
        JSON.stringify(event),
        {
          headers: { 
            'Content-Type': 'application/json'
          },
          params: {
            // Importante: incluir conferenceDataVersion para habilitar Meet
            conferenceDataVersion: 1
          }
        }
      );
      


      // O link do Meet vem na resposta
      const meetLink = response.data?.conferenceData?.entryPoints?.find(
        entry => entry.entryPointType === 'video'
      )?.uri || response.data?.meetLink;
      
      return meetLink;
    } catch (error) {
      console.error('Erro ao criar evento no Google Calendar:', error);
      throw error;
    }
  },

  /**
   * Cria um agendamento utilizando uma sessão de um pacote
   */
  async createPackageAppointment(message, closeModal, user, packageData) {
    try {
      console.log('Iniciando agendamento com pacote:', packageData);
      
      // Obter dados do paciente
      const { data: { user: userPatient } } = await supabase.auth.getUser();

      const { data: patient } = await supabase
        .from('patient')
        .select()
        .eq('id_user', userPatient.id);
      
      if (!patient || patient.length === 0) {
        return toast.error('Dados do paciente não encontrados');
      }
      
      // Verificar se há créditos de pacote disponíveis
      const creditBalance = await this.getPatientCreditBalance(patient[0].id, packageData.type);
      
      // Verificar especificamente créditos de pacote
      const packageCredits = creditBalance.transactions.filter(t => t.source_type === 'package');
      const packageAvailable = packageCredits.reduce((sum, t) => sum + t.amount, 0);
      
      if (packageAvailable <= 0) {
        return toast.error('Não há créditos de pacote disponíveis para este tipo de sessão');
      }
      
      // Calcular horários
      const endTime = moment(message.time, 'HH:mm').add(50, 'minutes').format('HH:mm');
      const startTime = moment(message.time, 'HH:mm').format('HH:mm');
      const dateDome = moment(message.selectedData).format('YYYY-MM-DD');
      
      const momentObjStart = moment(`${dateDome} ${startTime}`, 'YYYY-MM-DD HH:mm');
      const momentObjEnd = moment(`${dateDome} ${endTime}`, 'YYYY-MM-DD HH:mm');
      
      // Criar evento no Google Calendar
      const link = await this.createGoogleCalendarEvent(
        momentObjStart,
        momentObjEnd,
        message,
        user,
        patient[0],
        packageData.type || 'Terapia'
      );
      
      // Criar o agendamento
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointment')
        .upsert({
          specialist_id: message.idSpec,
          patient_id: patient[0].id,
          status: 'confirmed',
          start: momentObjStart.toISOString(),
          end: momentObjEnd.toISOString(),
          start_history: momentObjStart.toISOString(),
          end_history: momentObjEnd.toISOString(),
          category: packageData.type || 'Terapia',
          meetLink: link,
          package_id: packageData.id,
          is_package: true
        })
        .select();
      
      if (appointmentError || !appointment) {
        return toast.error('Erro ao registrar agendamento');
      }
      
      // Debitar crédito de pacote
      await this.debitPackageCredit(
        patient[0].id,
        packageData.type,
        appointment[0].id,
        packageData.id
      );
      
      // Notificar o paciente sobre o agendamento
      await this.sendAppointmentNotification(
        patient[0].id, 
        appointment[0].id, 
        message.idSpec,
        momentObjStart
      );
      
      message.setTimeMeeting && message.setTimeMeeting(message.time);
      toast.success("Agendamento com Pacote Concluído!");
      closeModal();
      window.location.href = "/consultas";
      
    } catch (error) {
      console.error('Erro ao criar agendamento com pacote:', error);
      toast.error('Algo deu errado, tente novamente!');
    }
  },

  /**
   * Debita um crédito de pacote
   */
  async debitPackageCredit(patientId, sessionType, appointmentId, packageId) {
    try {
      // Buscar carteira apropriada
      const { data: wallets } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId)
        .eq('type', sessionType)
        .limit(1);
      
      let walletId = wallets?.[0]?.id;
      
      // Se não encontrar carteira específica, buscar carteira genérica
      if (!walletId) {
        const { data: genericWallets } = await supabase
          .from('wallet')
          .select('*')
          .eq('patient_id', patientId)
          .limit(1);
        
        walletId = genericWallets?.[0]?.id;
      }
      
      if (!walletId) {
        throw new Error('Carteira não encontrada');
      }
      
      // Registrar transação de débito de pacote
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletId,
          amount: -1, // Débito de 1 crédito
          transaction_type: 'appointment',
          source_type: 'package',
          appointment_id: appointmentId,
          package_id: packageId,
          notes: `Agendamento de sessão ${sessionType} usando pacote`,
          created_at: new Date().toISOString()
        });
      
      if (transactionError) throw transactionError;
      
      return true;
    } catch (error) {
      console.error('Erro ao debitar crédito de pacote:', error);
      throw error;
    }
  },
  
  /**
   * Envia notificação sobre o agendamento
   */
  async sendAppointmentNotification(patientId, appointmentId, specialistId, appointmentDate) {
    try {
      const formattedDate = moment(appointmentDate).format('DD/MM/YYYY [às] HH:mm');
      
      // Buscar dados do especialista
      const { data: specialist } = await supabase
        .from('specialist')
        .select('name')
        .eq('id', specialistId)
        .single();
      
      // Criar notificação
      await supabase
        .from('notifications')
        .insert({
          user_id: patientId,
          title: 'Consulta Agendada',
          message: `Sua consulta com ${specialist?.name || 'especialista'} foi agendada para ${formattedDate}`,
          type: 'appointment',
          read: false,
          appointment_id: appointmentId,
          created_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
    }
  },
  
  /**
   * Obtém os pacotes ativos do paciente (mantido para compatibilidade)
   */
  async getActivePackages() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return [];
      
      const { data: patient } = await supabase
        .from('patient')
        .select('id')
        .eq('id_user', user.id)
        .single();
      
      if (!patient) return [];
      
      // Buscar pacotes ativos através das transações de crédito
      const { data: wallets } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patient.id);
      
      if (!wallets || wallets.length === 0) return [];
      
      const { data: transactions } = await supabase
        .from('credit_transactions')
        .select(`
          *,
          wallet:wallet_id(*)
        `)
        .in('wallet_id', wallets.map(w => w.id))
        .eq('source_type', 'package');
      
      // Agrupar por tipo de sessão e calcular saldos
      const packagesByType = {};
      
      (transactions || []).forEach(t => {
        const sessionType = t.wallet?.type || 'individual';
        
        if (!packagesByType[sessionType]) {
          packagesByType[sessionType] = {
            id: `package_${sessionType}`,
            type: sessionType,
            available_credits: 0,
            transactions: []
          };
        }
        
        packagesByType[sessionType].available_credits += t.amount;
        packagesByType[sessionType].transactions.push(t);
      });
      
      // Retornar apenas pacotes com créditos disponíveis
      return Object.values(packagesByType).filter(pkg => pkg.available_credits > 0);
      
    } catch (error) {
      console.error('Erro ao buscar pacotes ativos:', error);
      return [];
    }
  }
};